import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import {
  setAssetOrigins,
  webPageUrl,
  widgetFrameMessageSchema,
  type RenderReport,
  type WidgetAction,
  type WidgetAssetResult,
  type WidgetCaptureRequest,
} from '@inference.engine/widget-html/web';
import type { HtmlWidget } from '@inference.engine/widgets';
import { ENV } from '@/lib/config/env';
import { postRenderReport } from '../../api/render-reports.api';
import { assetImageSrc } from '../../lib/asset-image';
import { buildSrcdoc } from '../../lib/build-srcdoc';
import { widgetFontFaces } from '../../lib/font-faces';
import { useDocumentDark } from '../../model/useDocumentDark';
import { DatasetTables } from './DatasetTables';

/** How long the frame may stay silent before its runtime is taken as failed to load. */
const RUNTIME_TIMEOUT_MS = 8000;
/** How long a capture may take before it is given up on. */
const CAPTURE_TIMEOUT_MS = 10000;

setAssetOrigins([ENV.ASSETS_API_BASE_URL]);

export interface HtmlWidgetFrameProps {
  widget: HtmlWidget;
  /** The stored version this frame shows; its render report is filed against it. */
  artifactId?: string;
  contextId?: string;
  onAction?: (action: WidgetAction) => void;
  /** Draws in this theme instead of the app's: a preview showing dark while the app is light. */
  theme?: 'light' | 'dark';
  /** Space around the widget, drawn in its own theme: a preview standing in for the chat around it. */
  inset?: number;
  /** Every render report the frame sends, for a page that measures the widget itself. */
  onReport?: (report: RenderReport) => void;
  /** Hands over a function that asks the widget for a PNG of itself, as a data URL. */
  onCaptureReady?: (capture: () => Promise<string>) => void;
}

const runtimeUrl = (hash: string) =>
  `${ENV.INFERENCE_ENGINE_API_BASE_URL.replace(/\/+$/, '')}/api/v1/widget-runtimes/${hash}`;

function Frame({
  widget,
  artifactId,
  contextId,
  onAction,
  theme,
  inset,
  onReport,
  onCaptureReady,
}: HtmlWidgetFrameProps) {
  const { t } = useTranslation('chats');
  const appDark = useDocumentDark();
  const isDark = theme ? theme === 'dark' : appDark;
  const pendingCaptures = useRef(
    new Map<string, { resolve: (png: string) => void; reject: (error: Error) => void }>(),
  );
  const frame = useRef<HTMLIFrameElement>(null);
  const reported = useRef<string | undefined>(undefined);
  const [height, setHeight] = useState<number>();
  const [answered, setAnswered] = useState(false);
  const [failed, setFailed] = useState(false);
  const token = useMemo(() => crypto.randomUUID().replace(/-/g, ''), []);

  const { srcdoc } = useMemo(
    () =>
      buildSrcdoc({
        document: widget.document,
        data: widget.data,
        runtimeUrl: runtimeUrl(widget.runtime),
        assetOrigin: new URL(ENV.ASSETS_API_BASE_URL).origin,
        theme: isDark ? 'dark' : 'light',
        // A forced theme can differ from the page's, so the page's surface cannot show through.
        surface: theme !== undefined,
        ...(inset ? { inset } : {}),
        token,
        labels: {
          noData: t('chats.htmlWidget.noData'),
          chartError: t('chats.htmlWidget.chartError'),
          all: t('chats.htmlWidget.all'),
        },
        fontFaces: widgetFontFaces(window.location.origin),
        fontOrigin: window.location.origin,
      }),
    [widget.document, widget.data, widget.runtime, isDark, theme, inset, token, t],
  );

  // A new page in the frame has yet to answer, and may load where the last one failed.
  const [page, setPage] = useState(srcdoc);
  if (page !== srcdoc) {
    setPage(srcdoc);
    setAnswered(false);
    setFailed(false);
  }

  useEffect(() => {
    if (answered) return undefined;
    const timer = setTimeout(() => {
      setFailed(true);
    }, RUNTIME_TIMEOUT_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [answered, srcdoc]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (!frame.current || event.source !== frame.current.contentWindow) return;
      // The frame may have been sent to another page, which has the same window but not the token.
      if ((event.data as { token?: unknown } | null)?.token !== token) return;
      const parsed = widgetFrameMessageSchema.safeParse(event.data);
      if (!parsed.success) return;
      setAnswered(true);
      const message = parsed.data;
      if (message.type === 'mf-widget:height') setHeight(message.height);
      else if (message.type === 'mf-widget:action') onAction?.(message.action);
      else if (message.type === 'mf-widget:link') {
        const url = webPageUrl(message.url);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
      } else if (message.type === 'mf-widget:asset') {
        const target = frame.current.contentWindow;
        const answer = (result: { src: string } | { error: string }) =>
          target?.postMessage(
            {
              type: 'mf-widget:asset-result',
              id: message.id,
              ...result,
            } satisfies WidgetAssetResult,
            '*',
          );
        assetImageSrc(message.assetId).then(
          (src) => answer({ src }),
          (error: unknown) =>
            answer({ error: error instanceof Error ? error.message : String(error) }),
        );
      } else if (message.type === 'mf-widget:capture-result') {
        const pending = pendingCaptures.current.get(message.id);
        pendingCaptures.current.delete(message.id);
        if (message.png) pending?.resolve(message.png);
        else pending?.reject(new Error(message.error ?? 'The widget could not be captured'));
      } else {
        onReport?.(message.report);
        if (artifactId && contextId && reported.current !== artifactId) {
          reported.current = artifactId;
          void postRenderReport(artifactId, contextId, message.report).catch(() => undefined);
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('message', onMessage);
    };
  }, [artifactId, contextId, onAction, onReport, token]);

  useEffect(() => {
    if (!onCaptureReady) return;
    const captures = pendingCaptures.current;
    onCaptureReady(
      () =>
        new Promise<string>((resolve, reject) => {
          const target = frame.current?.contentWindow;
          if (!target) {
            reject(new Error('The widget is not showing'));
            return;
          }
          const id = crypto.randomUUID();
          const timer = setTimeout(() => {
            captures.delete(id);
            reject(new Error('The widget took too long to capture'));
          }, CAPTURE_TIMEOUT_MS);
          captures.set(id, {
            resolve: (png) => {
              clearTimeout(timer);
              resolve(png);
            },
            reject: (error) => {
              clearTimeout(timer);
              reject(error);
            },
          });
          // The frame is sandboxed without an origin of its own, so it can only be addressed as '*'.
          target.postMessage({ type: 'mf-widget:capture', id } satisfies WidgetCaptureRequest, '*');
        }),
    );
  }, [onCaptureReady]);

  if (failed) return <DatasetTables data={widget.data} caption={t('chats.htmlWidget.fallback')} />;

  return (
    <iframe
      ref={frame}
      title={widget.title ?? t('chats.htmlWidget.title')}
      sandbox="allow-scripts"
      srcDoc={srcdoc}
      className="block w-full border-0"
      style={height === undefined ? undefined : { height: `${height}px` }}
    />
  );
}

/** An agent-made widget, sandboxed in its own frame on the runtime it was made with. */
export const HtmlWidgetFrame = memo(function HtmlWidgetFrame(props: HtmlWidgetFrameProps) {
  const { t } = useTranslation('chats');
  return (
    <ErrorBoundary
      resetKeys={[props.widget.document, props.widget.runtime]}
      fallback={<DatasetTables data={props.widget.data} caption={t('chats.htmlWidget.fallback')} />}
    >
      <Frame {...props} />
    </ErrorBoundary>
  );
});
